/**
 * Maps admin parcel history + cost API into PaymentSlip paymentData shape.
 */

function sumDetailSubtotals(details) {
  return (details || []).reduce(
    (sum, d) => sum + (Number(d.subTotal) || 0),
    0,
  );
}

function dimValue(d, parcelKey, itemKey) {
  if (d[parcelKey] != null && d[parcelKey] !== "") return String(d[parcelKey]);
  if (d[itemKey] != null && d[itemKey] !== "") return String(d[itemKey]);
  return "";
}

function pickField(obj, ...keys) {
  if (!obj || typeof obj !== "object") return "";
  for (const key of keys) {
    const v = obj[key];
    if (v != null && String(v).trim() !== "") return v;
  }
  return "";
}

function pickFrom(sources, ...keys) {
  for (const src of sources) {
    const v = pickField(src, ...keys);
    if (v !== "") return v;
  }
  return "";
}

/**
 * Build a readable street address from door / street / additional fields.
 * Prefer combining all available parts — do not stop at additional alone
 * (that often leaves only a door number).
 */
function composePartyStreetAddress(sources, role) {
  const prefix = role; // "sender" | "receiver"
  const Pascal = role.charAt(0).toUpperCase() + role.slice(1);

  const door = pickFrom(sources, `${prefix}DoorNo`, `${Pascal}DoorNo`);
  const street = pickFrom(
    sources,
    `${prefix}StreetName`,
    `${Pascal}StreetName`,
  );
  const additional = pickFrom(
    sources,
    `${prefix}AdditionalAddressInfo`,
    `${Pascal}AdditionalAddressInfo`,
  );
  const fallbackAddress = pickFrom(
    sources,
    `${prefix}Address`,
    `${Pascal}Address`,
  );

  const parts = [];
  const doorStreet = [door, street].filter(Boolean).join(", ");
  if (doorStreet) parts.push(doorStreet);

  if (additional) {
    const add = String(additional).trim();
    // Skip if additional is already covered by door/street
    const alreadyShown =
      (door && add === String(door).trim()) ||
      (street && add === String(street).trim()) ||
      (doorStreet && add === doorStreet);
    if (!alreadyShown) parts.push(add);
  }

  if (parts.length) return parts.join(", ");
  if (fallbackAddress) return String(fallbackAddress).trim();
  return "";
}

function pickLocationId(sources, ...keys) {
  return pickFrom(sources, ...keys);
}

export function buildAdminPaymentSlipData(parcel, costData = {}) {
  const details = parcel?.details || [];
  const first = details[0] || {};
  const sources = [costData, parcel, first];

  const subTotal = Number(costData.subTotal ?? sumDetailSubtotals(details));
  const vatAmount = Number(costData.vaT_TaxAmount ?? 0);
  const discountAmount = Number(costData.discountAmount ?? 0);

  const currencyType = first.currencyType || costData.currencyType || "GBP";
  const symbol = first.symbol || costData.symbol || "£";

  const hasStripe = Boolean(parcel?.stripePaymentIntentId);
  const paymentTypeId = hasStripe
    ? "2"
    : String(costData.paymentTypeMethodId || "5");

  const receiverName =
    pickField(parcel, "receiverName", "ReceiverName") ||
    pickField(costData, "receiverName", "ReceiverName") ||
    "";
  const receiverEmail =
    pickField(parcel, "receiverEmail", "ReceiverEmail") ||
    pickField(costData, "receiverEmail", "ReceiverEmail") ||
    "";
  const receiverPhone =
    pickField(
      parcel,
      "receiverPhone",
      "receiverMobileNumber",
      "receiverMobileNo",
      "ReceiverPhone",
      "ReceiverMobileNo",
    ) ||
    pickField(
      costData,
      "receiverPhone",
      "receiverMobileNumber",
      "receiverMobileNo",
      "ReceiverPhone",
      "ReceiverMobileNo",
    ) ||
    "";
  const receiverPostCode =
    pickField(parcel, "receiverPostCode", "ReceiverPostCode") ||
    pickField(costData, "receiverPostCode", "ReceiverPostCode") ||
    "";
  // Same layout as customer order slip — street/additional only (no postcode join)
  const receiverAddress = composePartyStreetAddress(sources, "receiver");

  const senderPostCode =
    pickField(parcel, "senderPostCode", "SenderPostCode") ||
    pickField(costData, "senderPostCode", "SenderPostCode") ||
    "";
  const senderAddress = composePartyStreetAddress(sources, "sender");

  return {
    parcelId: parcel.parcelNo,
    recordSerialNo:
      costData.recordSerialNo ||
      costData.parcelRecordSerialNo ||
      String(parcel.parcelNo),
    bookingBarcodeNumber:
      costData.recordSerialNo ||
      costData.barcode ||
      String(parcel.parcelNo),
    currencyType,
    currencyTypeId: costData.currencyTypeId,
    currencyTypes: costData.currencyTypes,
    senderName: parcel.senderName || "",
    senderEmail: parcel.senderEmail || "",
    senderMobileNumber: parcel.senderPhone || "",
    senderPostCode,
    senderAddress,
    senderCity: pickLocationId(
      sources,
      "senderCityId",
      "SenderCityId",
      "senderCity",
      "SenderCity",
    ),
    senderCountry: pickLocationId(
      sources,
      "senderCountryId",
      "SenderCountryId",
      "senderCountry",
      "SenderCountry",
    ),
    receiverName,
    receiverEmail,
    receiverMobileNumber: receiverPhone,
    receiverPostCode,
    receiverAddress,
    receiverCity: pickLocationId(
      sources,
      "receiverCityId",
      "ReceiverCityId",
      "receiverCity",
      "ReceiverCity",
    ),
    receiverCountry: pickLocationId(
      sources,
      "receiverCountryId",
      "ReceiverCountryId",
      "receiverCountry",
      "ReceiverCountry",
    ),
    paymentTypeId,
    stripePaymentIntentId: parcel.stripePaymentIntentId || "",
    stripePaymentMethod: parcel.stripePaymentMethod || "",
    stripeChargeId: parcel.stripeChargeId || "",
    cardBrand: parcel.cardBrand || "",
    cardLast4: parcel.cardLast4 || "",
    preset: first.dimensionName || "",
    productLength: dimValue(first, "parcelLength", "itemLength"),
    productWidth: dimValue(first, "parcelWidth", "itemWidth"),
    productHeight: dimValue(first, "parcelHeight", "itemHeight"),
    productWeight:
      dimValue(first, "parcelWeight", "itemWeight") ||
      first.weightDescription ||
      "",
    producDetails: details.map((d) => ({
      productGroupName:
        d.itemCategoryName || d.productGroupName || d.cargoTypeName || "—",
      productTypeName:
        d.itemTypeName ||
        d.productTypeName ||
        d.shippingServiceName ||
        d.routeName ||
        "—",
      itemDescription:
        d.uniqItemDescription ||
        d.itemDescription ||
        d.parcelAdditionalInfo ||
        "—",
      productValue: String(
        Number(d.itemValue ?? d.productValue ?? d.subTotal ?? 0) || 0,
      ),
      productQty: Math.max(1, Number(d.productQty) || 1),
      preset: d.dimensionName || d.preset || "",
      itemLength: dimValue(d, "parcelLength", "itemLength"),
      itemWidth: dimValue(d, "parcelWidth", "itemWidth"),
      itemHeight: dimValue(d, "parcelHeight", "itemHeight"),
      itemWeight:
        dimValue(d, "parcelWeight", "itemWeight") || d.weightDescription || "",
    })),
    transitCost: [
      {
        totalCost: subTotal + vatAmount,
        totalCostWithoutVAT: subTotal,
        totalCostWithVAT: subTotal + vatAmount,
        vatPercentageAmount: vatAmount,
        disCountedPercentageAmount: discountAmount,
        disCountedPercentage: costData.discountPercentage || 0,
        afterDiscountCost: Math.max(0, subTotal - discountAmount),
      },
    ],
    _adminMeta: {
      routeName: first.routeName,
      parcelStatusName: parcel.parcelStatusName,
      paymentStatus: first.paymentStatus,
      symbol,
      stripePaymentIntentId: parcel.stripePaymentIntentId,
      stripeChargeId: parcel.stripeChargeId,
    },
  };
}
